import { useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import { Header } from "@/components/Header";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Bell, Trash2, Plus, Loader2 } from "lucide-react";
import { getLoginUrl } from "@/const";

export default function Monitor() {
  const { t } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  const [domain, setDomain] = useState("");
  const [selectedTld, setSelectedTld] = useState(".com");

  const { data: monitors, refetch: refetchMonitors } = trpc.monitor.list.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  const addMonitor = trpc.monitor.add.useMutation({
    onSuccess: () => {
      toast.success(t.monitor.addSuccess);
      setDomain("");
      refetchMonitors();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const deleteMonitor = trpc.monitor.delete.useMutation({
    onSuccess: () => {
      toast.success(t.monitor.deleteSuccess);
      refetchMonitors();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleAdd = () => {
    if (!domain.trim()) {
      toast.error("Please enter a domain name");
      return;
    }
    addMonitor.mutate({ domain: domain.trim(), tld: selectedTld });
  };

  const handleDelete = (id: number) => {
    deleteMonitor.mutate({ id });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "monitoring":
        return <Badge variant="secondary">{t.monitor.monitoring}</Badge>;
      case "available":
        return <Badge className="bg-green-500/20 text-green-500 hover:bg-green-500/30">{t.monitor.available}</Badge>;
      case "notified":
        return <Badge variant="outline">{t.monitor.notified}</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 4rem)' }}>
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>{t.monitor.title}</CardTitle>
              <CardDescription>Please login to use domain monitoring</CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild className="w-full">
                <a href={getLoginUrl()}>Login</a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
            <Bell className="h-8 w-8 text-primary" />
            {t.monitor.title}
          </h1>
          <p className="text-muted-foreground">{t.monitor.subtitle}</p>
        </div>

        {/* Add Domain Form */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{t.monitor.addDomain}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Input
                placeholder={t.monitor.domainPlaceholder}
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                className="flex-1 w-full"
              />
              <Select value={selectedTld} onValueChange={setSelectedTld}>
                <SelectTrigger className="w-full sm:w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=".com">.com</SelectItem>
                  <SelectItem value=".net">.net</SelectItem>
                  <SelectItem value=".org">.org</SelectItem>
                  <SelectItem value=".io">.io</SelectItem>
                  <SelectItem value=".ai">.ai</SelectItem>
                  <SelectItem value=".co">.co</SelectItem>
                  <SelectItem value=".app">.app</SelectItem>
                  <SelectItem value=".dev">.dev</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleAdd} disabled={addMonitor.isPending} className="w-full sm:w-auto">
                {addMonitor.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    {t.monitor.addDomain}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Monitoring List */}
        <Card>
          <CardHeader>
            <CardTitle>{t.monitor.monitoring}</CardTitle>
          </CardHeader>
          <CardContent>
            {!monitors || monitors.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                {t.monitor.noMonitors}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t.table.tld}</TableHead>
                    <TableHead>Domain</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Checked</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monitors.map((monitor) => (
                    <TableRow key={monitor.id}>
                      <TableCell className="font-medium">{monitor.tld}</TableCell>
                      <TableCell className="font-mono">{monitor.domain}{monitor.tld}</TableCell>
                      <TableCell>{getStatusBadge(monitor.status)}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {monitor.lastChecked ? new Date(monitor.lastChecked).toLocaleString() : "Not checked yet"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(monitor.id)}
                          disabled={deleteMonitor.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
